import { useState, useEffect, useRef } from 'react';
import api, { LOCATIONS, PROJECT_TYPES } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Plus, Eye, EyeOff, Star, Trash2, Image as ImageIcon, Loader2, RefreshCw,
  Pencil, ArrowUp, ArrowDown, Upload, X, Copy,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_PROJECT = {
  title: '', subtitle: '', description_long: '', description: '',
  location: '', project_type: 'new_build',
  budget_range: '', style: '', images: [], featured: false, published: true,
};

// Resolve relative /api/uploads/... URLs to the full backend URL so <img>
// renders correctly from the React dev server / preview origin.
const resolveImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/')) return `${API_URL}${url}`;
  return url;
};

// ============================================================
// Image upload zone — multi-file, drag & drop, thumbnail previews,
// per-image delete and reorder. Stays as a controlled component on
// `images: string[]` so callers don't need to know about File objects.
// ============================================================
function ImageUploadZone({ images, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const inputRef = useRef(null);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(f => f.type.startsWith('image/'));
    if (!files.length) return;
    setUploading(true);
    setUploadProgress({ done: 0, total: files.length });
    const next = [...images];
    let failures = 0;
    for (const file of files) {
      try {
        if (file.size > 10 * 1024 * 1024) {
          throw new Error('Image exceeds 10 MB limit');
        }
        const fd = new FormData();
        fd.append('file', file);
        // Use the shared axios `api` instance so the auth interceptors fire
        // (Bearer fallback when third-party cookies are blocked, e.g. Safari
        // ITP / incognito). Setting Content-Type to undefined lets axios fill
        // in the correct `multipart/form-data; boundary=...` header.
        // 60s per-file timeout so slow SA connections don't get axios' default 0/no-timeout hangs.
        const { data } = await api.post('/studio/uploads/image', fd, {
          headers: { 'Content-Type': undefined },
          timeout: 60000,
        });
        next.push(data.url);
      } catch (e) {
        failures += 1;
        const msg = e.response?.data?.detail || e.message || 'upload failed';
        toast.error(`${file.name}: ${msg}`);
      }
      setUploadProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    onChange(next);
    setUploading(false);
    setUploadProgress({ done: 0, total: 0 });
    if (files.length > 1 && failures === 0) {
      toast.success(`Uploaded ${files.length} image${files.length === 1 ? '' : 's'}`);
    }
  };

  const removeAt = (idx) => {
    const next = images.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  const moveImage = (idx, dir) => {
    const next = images.slice();
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  return (
    <div data-testid="image-upload-zone">
      <label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 block mb-2">
        Project Images
      </label>

      {/* Drag/Drop area */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          uploadFiles(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-sm p-6 text-center cursor-pointer transition-colors ${
          dragOver
            ? 'border-brand-accent bg-brand-accent/5'
            : 'border-brand-dark/15 hover:border-brand-dark/30 bg-brand-light/40'
        }`}
        data-testid="image-drop-zone"
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => uploadFiles(e.target.files)}
          data-testid="image-file-input"
        />
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-brand-dark/60 text-sm" data-testid="upload-progress">
            <Loader2 className="w-4 h-4 animate-spin" />
            {uploadProgress.total > 1
              ? `Uploading ${uploadProgress.done + 1} of ${uploadProgress.total}...`
              : 'Uploading...'}
          </div>
        ) : (
          <>
            <Upload className="w-6 h-6 text-brand-dark/30 mx-auto mb-2" />
            <p className="text-sm font-body text-brand-dark/70">
              Drop images here, or <span className="text-brand-accent-dark underline">click to browse</span>
            </p>
            <p className="text-[11px] text-brand-dark/40 font-body mt-1">
              JPG, PNG, WebP — up to 10 MB each. The first image is the cover.
            </p>
          </>
        )}
      </div>

      {/* Thumbnail grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 mt-4" data-testid="image-thumbnails">
          {images.map((url, i) => (
            <div key={`${url}-${i}`} className="relative group rounded-sm overflow-hidden border border-black/10 bg-brand-light"
                 data-testid={`image-thumb-${i}`}>
              <img src={resolveImageUrl(url)} alt={`Project image ${i + 1}`} className="w-full h-24 object-cover" />
              {i === 0 && (
                <span className="absolute top-1 left-1 bg-brand-dark/80 text-white text-[10px] font-body uppercase tracking-wider px-1.5 py-0.5 rounded-sm">
                  Cover
                </span>
              )}
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-between p-1 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  type="button"
                  onClick={() => moveImage(i, -1)}
                  disabled={i === 0}
                  title="Move left"
                  className="text-white/80 hover:text-white disabled:opacity-30 p-0.5"
                  data-testid={`image-move-left-${i}`}
                >
                  <ArrowUp className="w-3.5 h-3.5 -rotate-90" />
                </button>
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  title="Remove"
                  className="text-white/80 hover:text-red-300 p-0.5"
                  data-testid={`image-delete-${i}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => moveImage(i, 1)}
                  disabled={i === images.length - 1}
                  title="Move right"
                  className="text-white/80 hover:text-white disabled:opacity-30 p-0.5"
                  data-testid={`image-move-right-${i}`}
                >
                  <ArrowUp className="w-3.5 h-3.5 rotate-90" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Modal form for adding or editing a portfolio project.
// `project` populated → edit mode (PUT). `seed` populated and `project` null
// → create mode prefilled (used by Duplicate). Both null → blank create.
// ============================================================
function ProjectFormModal({ open, project, seed, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_PROJECT);
  const [saving, setSaving] = useState(false);
  const isEdit = !!project;

  useEffect(() => {
    if (open) {
      if (project) {
        setForm({ ...EMPTY_PROJECT, ...project, images: project.images || [] });
      } else if (seed) {
        setForm({ ...EMPTY_PROJECT, ...seed, images: seed.images || [] });
      } else {
        setForm({ ...EMPTY_PROJECT });
      }
    }
  }, [open, project, seed]);

  if (!open) return null;

  const update = (patch) => setForm(p => ({ ...p, ...patch }));

  const handleSave = async () => {
    if (!form.title || !form.location || !form.project_type) {
      toast.error('Title, location and type are required');
      return;
    }
    setSaving(true);
    try {
      // Strip backend-managed fields to keep PUT body clean
      const { project_id, _id, created_at, created_by, updated_at, ...payload } = form;
      if (isEdit) {
        await api.put(`/projects/${project.project_id}`, payload);
        toast.success('Project updated');
      } else {
        await api.post('/projects', payload);
        toast.success('Project created');
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto"
         data-testid="project-form-modal">
      <div className="bg-white rounded-sm border border-black/10 max-w-3xl w-full my-8 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-black/10 sticky top-0 bg-white z-10">
          <h3 className="font-heading text-base font-medium text-brand-dark">
            {isEdit ? 'Edit project' : 'New project'}
          </h3>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="project-modal-close">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 block mb-1">Title *</label>
              <Input data-testid="modal-project-title" value={form.title}
                onChange={(e) => update({ title: e.target.value })} className="rounded-sm h-10" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 block mb-1">Location *</label>
              <Select value={form.location} onValueChange={(v) => update({ location: v })}>
                <SelectTrigger data-testid="modal-project-location" className="rounded-sm h-10">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {LOCATIONS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 block mb-1">Type *</label>
              <Select value={form.project_type} onValueChange={(v) => update({ project_type: v })}>
                <SelectTrigger data-testid="modal-project-type" className="rounded-sm h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROJECT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 block mb-1">Budget Range</label>
              <Input data-testid="modal-project-budget" value={form.budget_range || ''}
                onChange={(e) => update({ budget_range: e.target.value })}
                placeholder="e.g. R5M - R10M" className="rounded-sm h-10" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 block mb-1">Style</label>
              <Input data-testid="modal-project-style" value={form.style || ''}
                onChange={(e) => update({ style: e.target.value })}
                placeholder="e.g. Contemporary" className="rounded-sm h-10" />
            </div>
            <div className="flex items-end gap-4 pt-1">
              <label className="flex items-center gap-2 text-sm text-brand-dark/70 cursor-pointer" data-testid="modal-project-featured-toggle">
                <input type="checkbox" checked={!!form.featured}
                  onChange={(e) => update({ featured: e.target.checked })}
                  className="h-4 w-4 accent-brand-accent" />
                Featured
              </label>
              <label className="flex items-center gap-2 text-sm text-brand-dark/70 cursor-pointer" data-testid="modal-project-published-toggle">
                <input type="checkbox" checked={!!form.published}
                  onChange={(e) => update({ published: e.target.checked })}
                  className="h-4 w-4 accent-brand-accent" />
                Published
              </label>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 block mb-1">Subtitle</label>
            <Input data-testid="modal-project-subtitle" value={form.subtitle || ''}
              onChange={(e) => update({ subtitle: e.target.value })}
              placeholder="Short caption shown under the main image"
              className="rounded-sm h-10 font-body text-sm" />
            <p className="text-[11px] text-brand-dark/40 font-body mt-1">
              Single line — used directly under the cover image on the public site.
            </p>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 block mb-1">Long Description</label>
            <Textarea data-testid="modal-project-description-long" value={form.description_long || ''}
              onChange={(e) => update({ description_long: e.target.value })}
              placeholder="Tell the full project story. Use a blank line between paragraphs."
              className="rounded-sm min-h-[200px] font-body text-sm" />
            <p className="text-[11px] text-brand-dark/40 font-body mt-1">
              Renders as a readable paragraph block below the subtitle. Blank lines separate paragraphs.
            </p>
          </div>

          <ImageUploadZone images={form.images || []} onChange={(imgs) => update({ images: imgs })} />
        </div>

        <div className="flex items-center justify-end gap-3 p-5 border-t border-black/10 bg-brand-light/30 sticky bottom-0">
          <Button variant="ghost" onClick={onClose} className="text-sm">Cancel</Button>
          <Button data-testid="modal-project-save" onClick={handleSave} disabled={saving}
            className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-xs uppercase tracking-wider">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isEdit ? 'Save changes' : 'Create project')}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Top-level Portfolio editor — replaces the inline portfolio block
// previously embedded in AdminDashboard.
// ============================================================
export default function PortfolioEditor() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [seedData, setSeedData] = useState(null);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/projects/all');
      setProjects(data);
    } catch {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProjects(); }, []);

  const togglePublish = async (p) => {
    try {
      await api.put(`/projects/${p.project_id}`, { published: !p.published });
      toast.success(p.published ? 'Unpublished' : 'Published');
      fetchProjects();
    } catch { toast.error('Failed to update'); }
  };

  const toggleFeatured = async (p) => {
    try {
      await api.put(`/projects/${p.project_id}`, { featured: !p.featured });
      toast.success(p.featured ? 'Removed from featured' : 'Marked as featured');
      fetchProjects();
    } catch { toast.error('Failed to update'); }
  };

  const removeProject = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/projects/${p.project_id}`);
      toast.success('Project deleted');
      fetchProjects();
    } catch { toast.error('Failed to delete'); }
  };

  const moveProject = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= projects.length) return;
    const next = projects.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    setProjects(next); // optimistic re-render
    try {
      await api.patch('/projects/reorder', { ordered_ids: next.map(p => p.project_id) });
    } catch {
      toast.error('Failed to reorder');
      fetchProjects();
    }
  };

  const openCreate = () => { setEditTarget(null); setSeedData(null); setModalOpen(true); };
  const openEdit = (p) => { setEditTarget(p); setSeedData(null); setModalOpen(true); };

  // Duplicate: prefill the modal with a copy of the source project (create mode).
  // Save → POST → backend auto-positions the new record at the end of the list.
  const duplicateProject = (p) => {
    const { project_id, _id, created_at, created_by, updated_at, position, ...rest } = p;
    setSeedData({
      ...rest,
      title: `${p.title || 'Untitled'} (Copy)`,
      images: Array.isArray(p.images) ? [...p.images] : [],
    });
    setEditTarget(null);
    setModalOpen(true);
  };

  return (
    <div data-testid="portfolio-management">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-brand-dark/50 font-body" data-testid="portfolio-count">
          {projects.length} project{projects.length === 1 ? '' : 's'}
        </p>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={fetchProjects} data-testid="refresh-projects">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" onClick={openCreate} data-testid="add-project-btn"
            className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-xs uppercase tracking-wider">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Project
          </Button>
        </div>
      </div>

      {projects.length === 0 ? (
        <div className="bg-white rounded-sm border border-black/10 p-12 text-center">
          <ImageIcon className="w-10 h-10 text-brand-dark/15 mx-auto mb-4" />
          <h3 className="font-heading text-lg font-medium text-brand-dark mb-2">No projects yet</h3>
          <p className="text-brand-dark/40 font-body text-sm">
            Add your first project. Published projects appear automatically on the public website.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {projects.map((project, idx) => (
            <div key={project.project_id}
              className="bg-white rounded-sm border border-black/10 p-4 flex items-center gap-4"
              data-testid={`studio-project-${project.project_id}`}>
              {/* Position + reorder */}
              <div className="flex flex-col items-center gap-0 w-7 flex-shrink-0">
                <button onClick={() => moveProject(idx, -1)} disabled={idx === 0}
                  title="Move up" className="text-brand-dark/30 hover:text-brand-dark disabled:opacity-20 p-0.5"
                  data-testid={`project-move-up-${project.project_id}`}>
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <span className="text-[10px] font-body text-brand-dark/40 leading-none my-0.5">{idx + 1}</span>
                <button onClick={() => moveProject(idx, 1)} disabled={idx === projects.length - 1}
                  title="Move down" className="text-brand-dark/30 hover:text-brand-dark disabled:opacity-20 p-0.5"
                  data-testid={`project-move-down-${project.project_id}`}>
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Thumbnail */}
              <div className="w-20 h-14 rounded-sm overflow-hidden flex-shrink-0 bg-brand-light">
                {project.images?.[0] ? (
                  <img src={resolveImageUrl(project.images[0])} alt={project.title}
                       className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="w-5 h-5 text-brand-dark/20" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-heading text-sm font-medium truncate">{project.title}</h4>
                  {project.featured && <Star className="w-3.5 h-3.5 text-brand-accent fill-brand-accent flex-shrink-0" />}
                  {!project.published && <Badge variant="outline" className="text-xs text-red-500 border-red-200">Draft</Badge>}
                </div>
                <p className="text-xs text-brand-dark/40 font-body capitalize">
                  {(LOCATIONS.find(l => l.value === project.location)?.label) || project.location}
                  {' · '}{(project.project_type || '').replace('_', ' ')}
                  {project.budget_range ? ` · ${project.budget_range}` : ''}
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <Button variant="ghost" size="sm" onClick={() => openEdit(project)} title="Edit"
                  data-testid={`project-edit-${project.project_id}`} className="h-8 w-8 p-0">
                  <Pencil className="w-4 h-4 text-brand-dark/50" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => duplicateProject(project)} title="Duplicate"
                  data-testid={`project-duplicate-${project.project_id}`} className="h-8 w-8 p-0">
                  <Copy className="w-4 h-4 text-brand-dark/50" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => togglePublish(project)} title={project.published ? 'Unpublish' : 'Publish'}
                  data-testid={`project-toggle-publish-${project.project_id}`} className="h-8 w-8 p-0">
                  {project.published ? <Eye className="w-4 h-4 text-green-600" /> : <EyeOff className="w-4 h-4 text-brand-dark/30" />}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => toggleFeatured(project)} title={project.featured ? 'Unfeature' : 'Feature'}
                  data-testid={`project-toggle-featured-${project.project_id}`} className="h-8 w-8 p-0">
                  <Star className={`w-4 h-4 ${project.featured ? 'text-brand-accent fill-brand-accent' : 'text-brand-dark/30'}`} />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => removeProject(project)} title="Delete"
                  data-testid={`project-delete-${project.project_id}`} className="h-8 w-8 p-0">
                  <Trash2 className="w-4 h-4 text-red-400" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ProjectFormModal
        open={modalOpen}
        project={editTarget}
        seed={seedData}
        onClose={() => setModalOpen(false)}
        onSaved={fetchProjects}
      />
    </div>
  );
}
